import {
	PropsWithChildren,
	ReactElement,
	ReactNode,
	useMemo,
	useState,
	useCallback,
	useRef,
	ReadonlyRefObject
} from "react";
import {
	setTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { flattenChildren, getReactNodeKey } from "client/Misc/reactUtils";
import styles from "./ReorderableList.module.less";
import { Space } from "../Space/Space";
import { localize } from "client_legacy/ndaw/culture";
import { Typography } from "../Typography/Typography";
import { ReorderableListItemDraggableMode, ReorderableListRowItem } from "./ReorderableListRowItem";
import { ReorderableListPanelItem } from "./ReorderableListPanelItem";
import { ReorderableListAddButton } from "./ReorderableListAddButton";
import { Separator } from "./Separator";
import { ReorderableListGroup, ReorderableListGroupDraggableMode } from "./ReorderableListGroup";
import { ReorderableListDragHandle } from "./ReorderableListDragHandle";
import { ReorderableListDragIndicator } from "./ReorderableListDragIndicator";
import {
	ReorderableListItemContext,
	ReorderableListItemContextData
} from "./ReorderableListItemContext";
import { ButtonBase } from "../ButtonBase/ButtonBase";
import classNames from "classnames";
import { Icon } from "../Icon/Icon";
import { Coordinate } from "client/Misc/commonTypes";
import { DragCallbackArgs } from "client/Hooks";
import { Portal } from "../Portal/Portal";
export type { ReorderableListAddButtonClickEventArgs } from "./ReorderableListAddButton";
export type { ReorderableListItemMouseInteractionConfiguration } from "./ReorderableListRowItem";

export type ReorderableListProps = {
	onItemMove?: (fromIndex: number, toIndex: number) => void;
	trackingId?: string;
	getOverlayText?: (activeIndex: number) => string;
	hasSeparator?: boolean;
};

export const ReorderableList = (props: PropsWithChildren<ReorderableListProps>): ReactElement => {
	const {
		onItemMove,
		trackingId,
		getOverlayText,
		hasSeparator = false,
		children
	} = props;

	const liRefs: ReadonlyRefObject<Map<number, HTMLLIElement>> = useRef<Map<number, HTMLLIElement>>(new Map());

	const [dragClientPosition, setDragClientPosition] = useState<Coordinate | null>(null);
	const [draggingItemIndex, setDraggingItemIndex] = useState<number | null>(null);
	const [targetItemIndex, setTargetItemIndex] = useState<number | null>(null);

	const childrenAsArray: ReactNode[] = flattenChildren(children);
	const numberOfChildren: number = childrenAsArray.length;

	const setLiRefs = useCallback(
		(itemIndex: number, ref: HTMLLIElement | null): void => {
			if (!ref) {
				liRefs.current
					.delete(itemIndex);
			} else {
				liRefs.current
					.set(itemIndex, ref);
			}
		},
		[]
	);

	const tooltipText = useMemo(
		(): string | undefined => isNotNil(draggingItemIndex)
			? getOverlayText?.(draggingItemIndex)
			: undefined,
		[
			getOverlayText,
			draggingItemIndex
		]
	);

	const dragHandler = useCallback(
		(draggingItemIndex: number, movement: Coordinate): void => {
			const targetItemIndex: number | undefined = getTargetItemIndex(
				draggingItemIndex,
				movement,
				liRefs,
				numberOfChildren
			);

			if (isNotNil(targetItemIndex)) {
				setTargetItemIndex(targetItemIndex);
			}
		},
		[numberOfChildren]
	);

	const dragStartHandler = useCallback(
		(index: number, args: DragCallbackArgs): void => {
			const {
				movement,
				pointerClientX,
				pointerClientY
			} = args;

			setDraggingItemIndex(index);
			setDragClientPosition({ x: pointerClientX, y: pointerClientY });
			dragHandler(index, movement);
		},
		[dragHandler]
	);

	const dragMoveHandler = useCallback(
		(index: number, args: DragCallbackArgs): void => {
			const {
				movement,
				pointerClientX,
				pointerClientY
			} = args;

			setDragClientPosition({ x: pointerClientX, y: pointerClientY });
			dragHandler(index, movement);
		},
		[dragHandler]
	);

	const dragStopHandler = useCallback(
		(draggingItemIndex: number, args: DragCallbackArgs): void => {

			const {
				movement
			} = args;

			const targetItemIndex: number | undefined = getTargetItemIndex(
				draggingItemIndex,
				movement,
				liRefs,
				numberOfChildren
			);

			if (isNil(targetItemIndex)) {
				throw new Error("There is no target item index");
			}

			if (draggingItemIndex !== targetItemIndex) {
				onItemMove?.(draggingItemIndex, targetItemIndex);
			}

			setDragClientPosition(null);
			setDraggingItemIndex(null);
			setTargetItemIndex(null);
			setDragClientPosition(null);
		},
		[
			onItemMove,
			numberOfChildren
		]
	);

	const itemContextData: readonly ReorderableListItemContextData[] = useMemo(
		() => {
			const result: ReorderableListItemContextData[] = [];
			for (let i = 0; i < numberOfChildren; i++) {
				result.push({
					onUp: i > 0 && onItemMove
						? () => onItemMove(i, i - 1)
						: undefined,
					onDown: i < numberOfChildren - 1 && onItemMove
						? () => onItemMove(i, i + 1)
						: undefined,
					isReorderEnabled: Boolean(onItemMove),
					isDragging: draggingItemIndex === i,
					dragStart: args => dragStartHandler(i, args),
					dragMove: args => dragMoveHandler(i, args),
					dragStop: args => dragStopHandler(i, args),
				});
			}

			return result;
		},
		[
			numberOfChildren,
			draggingItemIndex,
			dragMoveHandler,
			dragStartHandler,
			dragStopHandler,
			onItemMove
		]
	);

	const renderDragTooltip = (): ReactElement | null => {
		if (!dragClientPosition || isNil(draggingItemIndex) || isNil(tooltipText)) {
			return null;
		}

		return (
			<Portal container={document.body}>
				<div
					className={styles.tooltipContainer}
					style={{
						transform: `translate3d(
							${dragClientPosition.x + 12}px,
							${dragClientPosition.y + 12}px,
							0
						)`
					}}
				>
					<span className={styles.tooltip}>
						{localize("_Move")}
						<Space />
						<Typography weight="bold">
							{tooltipText}
						</Typography>
					</span>
				</div>
			</Portal>
		);
	};

	return (
		<>
			<ul
				className={styles.root}
				{...setTrackingId(trackingId)}
			>
				{hasSeparator && numberOfChildren > 0 && (
					<li><Separator /></li>
				)}
				{childrenAsArray.map(
					(x, i) => {
						const transformY: number | undefined = isNotNil(draggingItemIndex) && isNotNil(targetItemIndex)
							? getItemTransform({
								itemIndex: i,
								draggingItemIndex,
								targetItemIndex,
								itemsRefsMap: liRefs.current
							})
							: undefined;

						return (
							<li
								key={getReactNodeKey(x, i)}
								ref={(ref: HTMLLIElement | null) => setLiRefs(i, ref)}
							>
								<div
									style={isNotNil(transformY)
										? {
											transition: ".15s ease-out transform",
											transform: `translate3d(0, ${transformY}px, 0)`,
										}
										: undefined
									}
								>
									<ReorderableListItemContext.Provider value={itemContextData[i]}>
										{x}
									</ReorderableListItemContext.Provider>
									{hasSeparator && <Separator />}
								</div>
							</li>
						);
					}
				)}
			</ul>
			{renderDragTooltip()}
		</>
	);
};

ReorderableList.displayName = "ReorderableList";;

export type ReorderableListRowItemLikeProps = {
	hasSeparator?: boolean;
	dontEllipseText?: boolean;
	trackingId: string;
	noSpaceAroundContent?: boolean;
} & ({
	hasSeparator?: undefined;
} | {
	hasSeparator: true;
	isLastItem?: boolean;
});

const ReorderableListRowItemLike = (props: PropsWithChildren<ReorderableListRowItemLikeProps>): ReactElement => {
	const {
		hasSeparator = false,
		dontEllipseText = false,
		noSpaceAroundContent = false,
		trackingId,
		children
	} = props;

	return (
		<div>
			{hasSeparator && <Separator />}
			<div
				{...setTrackingId(trackingId)}
				className={styles.rowItem}
			>
				<ButtonBase
					disabled
					className={classNames(
						styles.dragHandle,
						styles.dragHandle_disabled
					)}
				>
					<Icon value="grip-lines" size="large" />
				</ButtonBase>
				<div className={classNames(
					styles.rowItemContent,
					{
						[styles.rowItemContent_ellipsed]: !dontEllipseText,
						[styles.rowItemContent_noSpaceAround]: noSpaceAroundContent
					}
				)}>
					{children}
				</div>
			</div>
			{(props.hasSeparator && props.isLastItem) && <Separator />}
		</div>
	);
};
ReorderableListRowItemLike.displayName = "ReorderableListRowItemLike";

ReorderableList.DragIndicator = ReorderableListDragIndicator;
ReorderableList.DragHandle = ReorderableListDragHandle;
ReorderableList.RowItem = ReorderableListRowItem;
ReorderableList.PanelItem = ReorderableListPanelItem;
ReorderableList.AddButton = ReorderableListAddButton;
ReorderableList.Group = ReorderableListGroup;
ReorderableList.ItemContext = ReorderableListItemContext;
ReorderableList.RowItemLike = ReorderableListRowItemLike;

export type ReorderableListItemDraggableModeForEditModeHandlers = {
	rowDraggableMode: ReorderableListItemDraggableMode;
	groupDraggableMode: ReorderableListGroupDraggableMode;
	onToggleEditMode: (isInEditMode: boolean) => void;
};

export function useReorderableListItemDraggableModeForEditMode(): ReorderableListItemDraggableModeForEditModeHandlers {
	const [isInEditMode, setIsInEditMode] = useState<boolean>(false);

	return {
		rowDraggableMode: isInEditMode
			? "handle"
			: "entire-row",
		groupDraggableMode: isInEditMode
			? "handle"
			: "header",
		onToggleEditMode: setIsInEditMode
	};
}

type GetItemTransformOptions = {
	itemIndex: number;
	draggingItemIndex: number;
	targetItemIndex: number;
	itemsRefsMap: Map<number, HTMLElement>;
};

function getItemTransform(
	options: GetItemTransformOptions
): number {
	const {
		itemIndex: currentItemIndex,
		draggingItemIndex,
		targetItemIndex,
		itemsRefsMap
	} = options;

	const isDraggingItem: boolean = currentItemIndex === draggingItemIndex;
	const isTargetItem: boolean = currentItemIndex === targetItemIndex;

	if (isDraggingItem && isTargetItem) {
		return 0;
	}

	const draggingItemRef: HTMLElement | undefined = itemsRefsMap.get(draggingItemIndex);

	if (!draggingItemRef) {
		return 0;
	}

	const draggingItemOffsetTop: number = draggingItemRef.offsetTop;
	const draggingItemHeight: number = draggingItemRef.offsetHeight;

	const targetItemRef: HTMLElement | undefined = itemsRefsMap.get(targetItemIndex);

	if (!targetItemRef) {
		return 0;
	}

	if (isDraggingItem) {
		const targetItemOffsetTop: number = targetItemRef.offsetTop;
		const targetItemHeight: number = targetItemRef.offsetHeight;

		return draggingItemIndex < targetItemIndex
			? (targetItemOffsetTop + targetItemHeight) - (draggingItemOffsetTop + draggingItemHeight)
			: targetItemOffsetTop - draggingItemOffsetTop;
	}

	if (draggingItemIndex < currentItemIndex && targetItemIndex >= currentItemIndex) {
		return - draggingItemHeight;
	}

	if (draggingItemIndex > currentItemIndex && targetItemIndex <= currentItemIndex) {
		return draggingItemHeight;
	}

	return 0;
}

function getTargetItemIndex(
	draggingItemIndex: number,
	movement: Coordinate,
	liRefs: ReadonlyRefObject<Map<number, HTMLElement>>,
	numberOfChildren: number
): number | undefined {
	const draggingItemRef: HTMLElement | undefined = liRefs.current
		.get(draggingItemIndex);

	if (!draggingItemRef) {
		return;
	}

	const draggingItemOffsetTopWithMovement: number = draggingItemRef.offsetTop + movement.y;
	const draggingItemHeight: number = draggingItemRef.offsetHeight;

	for (let i = 0; i < numberOfChildren; i++) {
		const itemRef: HTMLElement | undefined = liRefs.current
			.get(i);

		if (!itemRef) {
			continue;
		}

		const itemOffsetTop: number = itemRef.offsetTop;
		const itemMiddleHeight: number = itemRef.offsetHeight / 2;

		const isTargetItem: boolean = i >= draggingItemIndex
			? draggingItemOffsetTopWithMovement < itemOffsetTop + draggingItemHeight + itemMiddleHeight
			: draggingItemOffsetTopWithMovement < itemOffsetTop + itemMiddleHeight;

		if (isTargetItem) {
			return i;
		}
	}

	return numberOfChildren - 1;
}